<template>
  <div>
    <div class="mb-1 block text-xs text-gray-600">
      {{ name }}
      <sup v-if="required" class="top-0 text-sm font-bold text-red-400">*</sup>
    </div>
    <div
      :class="[
        !!isError ? 'border-red-400' : 'border-gray-600',
        disabled && 'pointer-events-none cursor-not-allowed opacity-40',
      ]"
      class="relative flex items-center justify-center overflow-hidden rounded border border-solid bg-gray-100"
    >
      <!-- <div v-if="uploadImageUrl" class="absolute left-0 top-0 h-full w-full">
        <figure class="absolute left-0 top-0 h-full w-full">
          <img
            :src="uploadImageUrl"
            alt="upload"
            loading="lazy"
            class="pointer-events-none absolute left-0 top-0 block h-full w-full object-cover"
          />
        </figure>
        <button
          class="absolute left-0 top-0 z-10 flex h-full w-full items-center justify-center bg-gray-600/30 opacity-0 duration-300 hover:opacity-100"
          @click="handleFileSelect"
        >
          <IconCamera class="h-6 w-6 text-gray-600" />
        </button>
        <button class="absolute right-2 top-2 z-20" @click="handleFileDelete">
          <IconClose class="h-6 w-6 text-black" />
        </button>
      </div> -->

      <div v-if="uploadFileName" class="flex w-full items-center p-2">
        <div
          class="flex flex-1 cursor-pointer items-center"
          @click="handleFileSelect"
        >
          <figure
            class="flex h-10 w-10 basis-10 items-center justify-center rounded bg-blue-100"
          >
            <IconProfile />
          </figure>
          <div class="flex-1 px-2">
            <p class="line-clamp-1 text-sm font-bold">
              {{ uploadFileName }}
            </p>
            <p class="line-clamp-1 text-sm text-gray-600">
              {{ uploadFileSizeText }}
            </p>
          </div>
        </div>
        <button
          class="flex h-8 w-8 basis-8 items-center justify-center text-gray-600 hover:text-blue-400"
          @click="handleFileDelete"
        >
          <IconClose />
        </button>
      </div>

      <div
        v-if="!uploadFileName"
        class="flex h-40 w-full items-center justify-center p-2"
      >
        <div class="text-center">
          <p class="text-gray-600">
            <label
              :for="disabled ? undefined : id"
              :title="t('browse')"
              class="cursor-pointer text-blue-400 hover:underline"
            >
              {{ t('browse') }}
            </label>
            {{ t('browse-file-text') }}
          </p>
          <p class="text-sm text-gray-500">{{ t('file-size-limit') }}</p>
          <p class="text-sm text-gray-500">{{ t('file-type-limit') }}</p>
        </div>
      </div>
    </div>

    <input
      :id="id"
      ref="inputFileRef"
      type="file"
      name="upload"
      accept=".png,.jpg,.jpeg,.csv"
      class="hidden"
      @change="handleFileUpload"
    />
  </div>
</template>

<script setup lang="ts">
  import { v4 as uuidv4 } from 'uuid';

  interface IProps {
    id: string;
    name: string;
    required?: boolean;
    disabled?: boolean;
    isError?: boolean;
    errorMessage?: string;
  }

  withDefaults(defineProps<IProps>(), {
    id: uuidv4(),
    name: '',
    required: false,
    disabled: false,
    isError: false,
    errorMessage: '',
  });

  const model = defineModel<File | undefined>({ default: undefined });

  const { t } = useI18n({ useScope: 'local' });
  const { $notification } = useNuxtApp();

  const inputFileRef = ref<HTMLInputElement | null>(null);
  const uploadFileName = ref<string>('');
  const uploadFileSize = ref<number>(0);
  const uploadImageUrl = ref<string>('');

  const uploadFileSizeText = computed((): string =>
    formatBytes(uploadFileSize.value)
  );

  const handleFileDelete = (): void => {
    if (inputFileRef.value) {
      inputFileRef.value.value = '';
    }
    uploadFileName.value = '';
    uploadFileSize.value = 0;
    uploadImageUrl.value = '';
    model.value = undefined;
  };

  const handleFileSelect = (): void => {
    inputFileRef.value?.click();
  };

  const handleFileUpload = ($event: Event): void => {
    const target = $event.target as HTMLInputElement;
    const files = target?.files;
    setUploadedFile(files);
  };

  const setUploadedFile = (files: FileList | undefined | null): void => {
    if (files && files?.length) {
      const file: File = files[0];

      // 上傳文件格式：JPG, PNG, CSV

      const isExtension = validateFileExtension(file.name, [
        'jpg',
        'jpeg',
        'png',
        'csv',
      ]);

      if (!isExtension) {
        $notification({
          message: 'File extension Error, .jpg, .png, and .csv only.',
          type: 'error',
        });
        if (inputFileRef.value) {
          inputFileRef.value.value = '';
        }
        return;
      }

      // 文件最大上限：5MB

      if (file.size >= 5242880) {
        $notification({
          message: 'File size exceeds maximum. Maximum file upload size: 5MB.',
          type: 'error',
        });
        if (inputFileRef.value) {
          inputFileRef.value.value = '';
        }
        return;
      }

      const reader: FileReader = new FileReader();

      reader.addEventListener('load', (e: ProgressEvent<FileReader>) => {
        const result: string = e.target?.result as string;
        uploadFileName.value = file.name;
        uploadFileSize.value = file.size;
        uploadImageUrl.value = result;
      });

      reader.readAsDataURL(file);
      model.value = file;
    }
  };
</script>

<i18n lang="json">
{
  "en-int": {
    "browse": "Browse file",
    "browse-file-text": "to upload",
    "file-size-limit": "Maximum file upload size: 5MB",
    "file-type-limit": "Accepted file types: JPG, PNG, CSV"
  },
  "en-jp": {
    "browse": "Browse file",
    "browse-file-text": "to upload",
    "file-size-limit": "Maximum file upload size: 5MB",
    "file-type-limit": "Accepted file types: JPG, PNG, CSV"
  },
  "en-tw": {
    "browse": "Browse file",
    "browse-file-text": "to upload",
    "file-size-limit": "Maximum file upload size: 5MB",
    "file-type-limit": "Accepted file types: JPG, PNG, CSV"
  },
  "en-kr": {
    "browse": "Browse file",
    "browse-file-text": "to upload",
    "file-size-limit": "Maximum file upload size: 5MB",
    "file-type-limit": "Accepted file types: JPG, PNG, CSV"
  },
  "en-americas": {
    "browse": "Browse file",
    "browse-file-text": "to upload",
    "file-size-limit": "Maximum file upload size: 5MB",
    "file-type-limit": "Accepted file types: JPG, PNG, CSV"
  },
  "en-asia-pacific": {
    "browse": "Browse file",
    "browse-file-text": "to upload",
    "file-size-limit": "Maximum file upload size: 5MB",
    "file-type-limit": "Accepted file types: JPG, PNG, CSV"
  },
  "en-emea": {
    "browse": "Browse file",
    "browse-file-text": "to upload",
    "file-size-limit": "Maximum file upload size: 5MB",
    "file-type-limit": "Accepted file types: JPG, PNG, CSV"
  },
  "ja-jp": {
    "browse": "ファイルを参照",
    "browse-file-text": "してアップロードする",
    "file-size-limit": "最大ファイルアップロードサイズ：5M",
    "file-type-limit": "使用可能なファイル形式：JPG、PNG、CSV"
  },
  "zh-cn": {
    "browse": "浏览",
    "browse-file-text": "要上传的文件",
    "file-size-limit": "文件最大上限：5MB",
    "file-type-limit": "上传文件格式：JPG , PNG, CSV"
  },
  "zh-tw": {
    "browse": "瀏覽",
    "browse-file-text": "要上傳的文件",
    "file-size-limit": "文件最大上限：5MB",
    "file-type-limit": "上傳文件格式：JPG , PNG, CSV"
  }
}
</i18n>
